import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getToken,
  setToken,
  clearToken,
  UNAUTHORIZED_EVENT,
} from "../api/request";
import { AuthResult } from "../api/auth";

export interface AuthUser {
  userId: number;
  email: string;
  onboardingCompleted: boolean;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** 登录/注册成功后写入凭证 */
  signIn: (result: AuthResult) => void;
  signOut: () => void;
}

const USER_STORAGE_KEY = "cloudstore_console_user";

function loadUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<AuthUser | null>(() => loadUser());

  const signIn = useCallback((result: AuthResult) => {
    const nextUser: AuthUser = {
      userId: result.userId,
      email: result.email,
      onboardingCompleted: result.onboardingCompleted,
    };
    setToken(result.token);
    try {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
    } catch {
      /* ignore */
    }
    setTokenState(result.token);
    setUser(nextUser);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    try {
      localStorage.removeItem(USER_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setTokenState(null);
    setUser(null);
  }, []);

  // 接口返回 401 时（token 缺失/过期）自动登出，ConsoleLayout 随即弹出登录框
  useEffect(() => {
    const onUnauthorized = () => signOut();
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [signOut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      isAuthenticated: !!token,
      signIn,
      signOut,
    }),
    [token, user, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
